import { formatMeasureValue } from '../lib/visualContract'
import { getMeasureMetadata } from '../lib/analyticalQuery'

/**
 * Line — two rendering modes over the same SVG foundation:
 *
 *   mode omitted / 'time_series' (default): one line per measure across a
 *     TimeSeriesResult's series (Phase 5A). Existing behavior, unchanged
 *     in shape — but see the null-handling fix below.
 *
 *   mode: 's_curve': three independent series (Planned Budget, Actual
 *     Cumulative, Forecast/EAC) across an SCurveResult's series (Phase
 *     5D composition of timeSeriesSSOTAdapter + plannedBudgetAdapter).
 *     This is a rendering mode on Line, NOT a separate VisualDefinition
 *     type — per the approved Phase 5D decision (type: 'line',
 *     mode: 's_curve').
 *
 * NULL SEMANTICS (both modes): a `null` value at a given time point means
 * that source has NO data there — it is never coerced to 0. The polyline
 * for a series is SPLIT into separate segments around any null gap,
 * rather than the previous behavior of drawing a false dip to zero. This
 * was a real bug in the prior time_series-only version of this
 * component (`p.value ?? 0` treated absence as zero) — fixed here for
 * BOTH modes, not just s_curve, since the same misrepresentation applied
 * there too.
 *
 * hasSnapshot (both modes, wherever it is meaningful — EAC/Actual points
 * only):
 *   true  → filled circle (genuine forecast_history snapshot)
 *   false → hollow circle (Not-Forecasted Budget fallback — Phase 4/5A)
 *   null  → no marker at all (no Actual/EAC source exists at this time
 *           point — distinct from "a source exists but is a fallback")
 *
 * This component performs NO financial calculation and imports NO
 * Adapter — every value it draws is read verbatim from the result prop.
 */
export default function Line({ result, yAxis, mode = 'time_series' }) {
  if (mode === 's_curve') return <SCurveChart result={result} />
  return <TimeSeriesChart result={result} yAxis={yAxis} />
}

// ─── Shared SVG geometry helpers ───────────────────────────────────────────
const CHART_WIDTH = 600
const CHART_HEIGHT = 200

function computeScale(allValues) {
  const nonNull = allValues.filter(v => v != null)
  const maxValue = Math.max(...nonNull, 1)
  const minValue = Math.min(...nonNull, 0)
  const range = maxValue - minValue || 1
  return { minValue, maxValue, range }
}

/**
 * Splits a series of {x, y} points (y may be null) into an array of
 * polyline segments, breaking at every null — never draws a line through
 * a gap, never coerces the gap to 0.
 */
function splitIntoSegments(points) {
  const segments = []
  let current = []
  for (const p of points) {
    if (p.y == null) {
      if (current.length > 0) segments.push(current)
      current = []
    } else {
      current.push(p)
    }
  }
  if (current.length > 0) segments.push(current)
  return segments
}

// ─── time_series mode (existing behavior, null bug fixed) ─────────────────
function TimeSeriesChart({ result, yAxis }) {
  const series = result?.series || []

  if (series.length === 0) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#9CA3AF', fontSize: 12.5 }}>No data available</div>
  }

  const COLORS = ['#1B3A6B', '#1A7A4A', '#B06D00', '#8B5CF6', '#EF4444']

  const seriesByMeasure = yAxis.map((m, i) => ({
    measureKey: m,
    color: COLORS[i % COLORS.length],
    points: series.map(point => ({
      time: point.time,
      hasSnapshot: point.hasSnapshot,
      value: point.rows?.[0]?.values?.[m] ?? null,
    })),
  }))

  const allValues = seriesByMeasure.flatMap(s => s.points.map(p => p.value))
  const { minValue, range } = computeScale(allValues)
  const xStep = series.length > 1 ? CHART_WIDTH / (series.length - 1) : 0
  const yFor = (v) => CHART_HEIGHT - ((v - minValue) / range) * CHART_HEIGHT

  return (
    <div>
      <div style={{ display: 'flex', gap: 14, marginBottom: 10 }}>
        {seriesByMeasure.map(s => (
          <div key={s.measureKey} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: s.color }} />
            {getMeasureMetadata(s.measureKey).label}
          </div>
        ))}
      </div>
      <svg viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`} style={{ width: '100%', height: 220 }}>
        {seriesByMeasure.map(s => {
          const geometryPoints = s.points.map((p, i) => ({ x: i * xStep, y: p.value }))
          const segments = splitIntoSegments(geometryPoints)
          return (
            <g key={s.measureKey}>
              {segments.map((seg, si) => (
                <polyline
                  key={si}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={2}
                  points={seg.map(pt => `${pt.x},${yFor(pt.y)}`).join(' ')}
                />
              ))}
              {s.points.map((p, i) => p.value == null ? null : (
                <circle
                  key={i}
                  cx={i * xStep}
                  cy={yFor(p.value)}
                  r={4}
                  fill={p.hasSnapshot === false ? '#fff' : s.color}
                  stroke={s.color}
                  strokeWidth={2}
                />
              ))}
            </g>
          )
        })}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#9CA3AF', marginTop: 4 }}>
        {series.map(point => <span key={point.time}>{point.time}</span>)}
      </div>
    </div>
  )
}

// ─── s_curve mode (Phase 5D) ────────────────────────────────────────────────
const S_CURVE_SERIES = [
  { key: 'planned_budget', label: 'Planned Budget', color: '#7C3AED', hasSnapshotAware: false },
  { key: 'actual', label: 'Actual (Cumulative)', color: '#1A7A4A', hasSnapshotAware: true },
  { key: 'eac', label: 'EAC (Forecast Snapshot)', color: '#B06D00', hasSnapshotAware: true },
]

function SCurveChart({ result }) {
  const series = result?.series || []

  if (series.length === 0) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#9CA3AF', fontSize: 12.5 }}>No data available</div>
  }

  const allValues = series.flatMap(p => [p.planned_budget, p.actual, p.eac])
  const { minValue, range } = computeScale(allValues)
  const xStep = series.length > 1 ? CHART_WIDTH / (series.length - 1) : 0
  const yFor = (v) => CHART_HEIGHT - ((v - minValue) / range) * CHART_HEIGHT

  return (
    <div>
      {/* Explicit legend clarifying what each curve represents — per the
          approved requirement to avoid any confusion between Planned
          (Project Timeline), Actual (cumulative-to-date), and EAC
          (point-in-time Forecast Revision snapshot). */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 4, flexWrap: 'wrap' }}>
        {S_CURVE_SERIES.map(s => (
          <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: s.color }} />
            {getMeasureMetadata(s.key).label}
          </div>
        ))}
      </div>
      <p style={{ fontSize: 10.5, color: '#9CA3AF', marginBottom: 10, lineHeight: 1.5 }}>
        Planned = monthly Project Timeline (Scheduling.jsx). Actual = cumulative cost to date at each
        Forecast Observation Date. EAC = Estimate at Completion as of each Forecast Revision snapshot —
        not a smoothed monthly value.
      </p>

      <svg viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`} style={{ width: '100%', height: 220 }}>
        {S_CURVE_SERIES.map(s => {
          const geometryPoints = series.map((p, i) => ({
            x: i * xStep,
            y: p[s.key],
            hasSnapshot: s.hasSnapshotAware ? p.hasSnapshot : null,
          }))
          const segments = splitIntoSegments(geometryPoints)
          return (
            <g key={s.key}>
              {segments.map((seg, si) => (
                <polyline
                  key={si}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={2}
                  points={seg.map(pt => `${pt.x},${yFor(pt.y)}`).join(' ')}
                />
              ))}
              {geometryPoints.map((p, i) => {
                if (p.y == null) return null
                // hasSnapshot === false → hollow (fallback). true → filled
                // (real snapshot). null (Planned series, or no Actual/EAC
                // source at this exact point) → filled normally, since
                // there is no fallback concept for Planned data at all.
                const isFallback = p.hasSnapshot === false
                return (
                  <circle
                    key={i}
                    cx={p.x}
                    cy={yFor(p.y)}
                    r={4}
                    fill={isFallback ? '#fff' : s.color}
                    stroke={s.color}
                    strokeWidth={2}
                  />
                )
              })}
            </g>
          )
        })}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#9CA3AF', marginTop: 4 }}>
        {series.map(point => <span key={point.time}>{point.time}</span>)}
      </div>

      {/* hasSnapshot legend — only relevant to Actual/EAC, explicitly
          scoped in the caption so it is not misread as applying to
          Planned (which has no snapshot concept). */}
      <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 10, color: '#6B7280' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#B06D00' }} />
          Real Forecast Snapshot
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', border: '2px solid #B06D00' }} />
          Fallback (no snapshot existed for this date)
        </div>
      </div>
    </div>
  )
}

